import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';
import Autocomplete from 'react-google-autocomplete';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Drawer from '~/components/Drawer';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useEffect, useState } from 'react';
import {
    createMedicalCenter,
    updateMedicalCenter,
} from '~/services/medical-centers';

const placesApiKey = import.meta.env.VITE_PLACES_API_KEY;

// Backend `MedicalCenter.location` accepts the long-form state name or
// the literal string 'Treatment Abroad'. Keep this list in sync with
// `validate_medical_center_location` in api/campaign/models.py.
const LOCATION_OPTIONS: { value: string; label: string }[] = [
    { value: 'Alabama', label: 'Alabama' },
    { value: 'Alaska', label: 'Alaska' },
    { value: 'Arizona', label: 'Arizona' },
    { value: 'Arkansas', label: 'Arkansas' },
    { value: 'California', label: 'California' },
    { value: 'Colorado', label: 'Colorado' },
    { value: 'Connecticut', label: 'Connecticut' },
    { value: 'Delaware', label: 'Delaware' },
    { value: 'District Of Columbia', label: 'District Of Columbia' },
    { value: 'Florida', label: 'Florida' },
    { value: 'Georgia', label: 'Georgia' },
    { value: 'Hawaii', label: 'Hawaii' },
    { value: 'Idaho', label: 'Idaho' },
    { value: 'Illinois', label: 'Illinois' },
    { value: 'Indiana', label: 'Indiana' },
    { value: 'Iowa', label: 'Iowa' },
    { value: 'Kansas', label: 'Kansas' },
    { value: 'Kentucky', label: 'Kentucky' },
    { value: 'Louisiana', label: 'Louisiana' },
    { value: 'Maine', label: 'Maine' },
    { value: 'Maryland', label: 'Maryland' },
    { value: 'Massachusetts', label: 'Massachusetts' },
    { value: 'Michigan', label: 'Michigan' },
    { value: 'Minnesota', label: 'Minnesota' },
    { value: 'Mississippi', label: 'Mississippi' },
    { value: 'Missouri', label: 'Missouri' },
    { value: 'Montana', label: 'Montana' },
    { value: 'Nebraska', label: 'Nebraska' },
    { value: 'Nevada', label: 'Nevada' },
    { value: 'New Hampshire', label: 'New Hampshire' },
    { value: 'New Jersey', label: 'New Jersey' },
    { value: 'New Mexico', label: 'New Mexico' },
    { value: 'New York', label: 'New York' },
    { value: 'North Carolina', label: 'North Carolina' },
    { value: 'North Dakota', label: 'North Dakota' },
    { value: 'Ohio', label: 'Ohio' },
    { value: 'Oklahoma', label: 'Oklahoma' },
    { value: 'Oregon', label: 'Oregon' },
    { value: 'Pennsylvania', label: 'Pennsylvania' },
    { value: 'Rhode Island', label: 'Rhode Island' },
    { value: 'South Carolina', label: 'South Carolina' },
    { value: 'South Dakota', label: 'South Dakota' },
    { value: 'Tennessee', label: 'Tennessee' },
    { value: 'Texas', label: 'Texas' },
    { value: 'Utah', label: 'Utah' },
    { value: 'Vermont', label: 'Vermont' },
    { value: 'Virginia', label: 'Virginia' },
    { value: 'Washington', label: 'Washington' },
    { value: 'West Virginia', label: 'West Virginia' },
    { value: 'Wisconsin', label: 'Wisconsin' },
    { value: 'Wyoming', label: 'Wyoming' },
    { value: 'Treatment Abroad', label: 'Treatment Abroad' },
];

const schema = yup.object().shape({
    name: yup.string().required('Name is required'),
    city: yup.string().required('City is required'),
    zipcode: yup.string().required('Zip code is required'),
    url: yup
        .string()
        .transform((v) => (v === '' ? undefined : v))
        .url('Must be a valid URL')
        .notRequired(),
    npi_num: yup.string().notRequired(),
    location: yup
        .string()
        .oneOf(
            LOCATION_OPTIONS.map((o) => o.value),
            'Please select a valid location',
        )
        .required('Location is required'),
});

interface FormType {
    name: string;
    city: string;
    zipcode: string;
    url?: string;
    location: string;
    npi_num?: string;
}

const defaultValues: FormType = {
    name: '',
    city: '',
    zipcode: '',
    url: '',
    location: '',
    npi_num: '',
};

interface DrawerProps {
    isOpen: boolean;
    setIsOpen: (v: boolean) => void;
    medCenter?: any;
    setMedCenter?: (v: any) => void;
    initialName?: string;
}

const EditMedicalCenterDrawer = ({
    isOpen,
    setIsOpen,
    medCenter,
    setMedCenter,
    initialName,
}: DrawerProps) => {
    const navigate = useNavigate();
    const isCreateMode = !medCenter;
    const [saving, setSaving] = useState(false);
    const {
        register,
        handleSubmit,
        control,
        setValue,
        formState: { errors },
        reset,
    } = useForm({
        defaultValues: defaultValues,
        resolver: yupResolver(schema),
    });

    const handlePlaceSelected = (place: any) => {
        if (!place) return;
        const components: any[] = place.address_components ?? [];
        const findComponent = (type: string) =>
            components.find((c) => c.types?.includes(type));

        const setIfChanged = (
            field: keyof FormType,
            value: string | undefined,
        ) => {
            if (value) {
                setValue(field, value, {
                    shouldDirty: true,
                    shouldValidate: true,
                });
            }
        };

        setIfChanged('name', place.name);
        setIfChanged('url', place.website);
        setIfChanged('city', findComponent('locality')?.long_name);
        setIfChanged('zipcode', findComponent('postal_code')?.long_name);

        const stateLong = findComponent(
            'administrative_area_level_1',
        )?.long_name;
        if (stateLong) {
            const match = LOCATION_OPTIONS.find((o) => o.value === stateLong);
            if (match) {
                setValue('location', match.value, {
                    shouldDirty: true,
                    shouldValidate: true,
                });
            }
        }
    };

    const onSubmit = (data: any) => {
        setSaving(true);
        const action = isCreateMode
            ? createMedicalCenter(data)
            : updateMedicalCenter(medCenter.id, data);

        action
            .then((res) => {
                if (isCreateMode) {
                    toast.success('Medical center created');
                    setIsOpen(false);
                    // The POST response is the new center serializer body. Navigate
                    // to its detail page so the user lands on the full record.
                    if (res?.id) {
                        navigate(`/medical-centers/${res.id}`);
                    }
                } else {
                    setMedCenter?.(res);
                    toast.success('Medical center updated');
                    setIsOpen(false);
                }
            })
            .catch((err) => {
                toast.error(
                    err?.message ??
                        (isCreateMode
                            ? 'Failed to create medical center'
                            : 'Failed to update medical center'),
                );
            })
            .finally(() => {
                setSaving(false);
            });
    };

    useEffect(() => {
        if (isCreateMode) {
            // Re-seed the form whenever the drawer opens in create mode so a
            // fresh name from the search step lands in the field.
            if (isOpen) {
                reset({ ...defaultValues, name: initialName ?? '' });
            }
            return;
        }
        if (medCenter) {
            const mappedMedCenter: FormType = {
                name: medCenter?.name ?? '',
                city: medCenter?.city ?? '',
                zipcode: medCenter?.zipcode ?? '',
                url: medCenter?.url ?? '',
                location: medCenter?.location ?? '',
                npi_num: medCenter?.npi_num ?? '',
            };
            reset(mappedMedCenter);
        }
    }, [medCenter, reset, isCreateMode, isOpen, initialName]);

    return (
        <Drawer
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            title={isCreateMode ? 'Add Medical Center' : 'Edit Medical Center'}
        >
            <div className=''>
                <form onSubmit={handleSubmit(onSubmit)}>
                    {placesApiKey && (
                        <div className='mb-4 mt-4'>
                            <label className='mb-1 block text-sm font-medium'>
                                Look up Medical Center
                            </label>
                            <Autocomplete
                                apiKey={placesApiKey}
                                onPlaceSelected={handlePlaceSelected}
                                options={{
                                    types: ['establishment'],
                                    componentRestrictions: { country: 'us' },
                                    fields: [
                                        'name',
                                        'address_components',
                                        'website',
                                    ],
                                }}
                                placeholder='Search by facility name…'
                                className='w-full rounded border border-gray-300 px-3 py-2'
                            />
                            <p className='mt-1 text-xs text-[#949599]'>
                                Pick a result to autofill the fields below.
                            </p>
                        </div>
                    )}

                    <div className='mb-4 mt-4'>
                        <label className='mb-1 block text-sm font-medium'>
                            Name
                        </label>
                        <input
                            {...register('name', { required: true })}
                            type='text'
                            className='w-full rounded border border-gray-300 px-3 py-2'
                            placeholder='Medical Center'
                        />
                        {errors.name && (
                            <p className='mt-1 text-xs text-red'>
                                {errors.name.message}
                            </p>
                        )}
                    </div>

                    {/* <div className='mb-4 mt-4'>
            <label htmlFor='aliases'>Aliases</label>
            <Controller
              name='aliases'
              control={control}
              render={({ field }) => (
                <CreatableSelect
                  isMulti
                  {...field}
                  onChange={(newValue) => field.onChange(newValue)}
                  placeholder='Add Alias'
                  styles={{
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: '#e0f7fa', // Adjust the background color
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: '#00796b', // Adjust the text color
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: '#00796b',
                      ':hover': {
                        backgroundColor: '#00796b',
                        color: 'white',
                      },
                    }),
                  }}
                />
              )}
            />
            {errors.aliases && (
              <p className='mt-1 text-xs text-red'>{errors.aliases.message}</p>
            )}
          </div> */}

                    <div className='mb-4 mt-4'>
                        <label className='mb-1 block text-sm font-medium'>
                            City
                        </label>
                        <input
                            {...register('city', { required: true })}
                            type='text'
                            className='w-full rounded border border-gray-300 px-3 py-2'
                            placeholder='City'
                        />
                        {errors.city && (
                            <p className='mt-1 text-xs text-red'>
                                {errors.city.message}
                            </p>
                        )}
                    </div>

                    <div className='mb-4 mt-4'>
                        <label className='mb-1 block text-sm font-medium'>
                            Location
                        </label>
                        <Controller
                            name='location'
                            control={control}
                            render={({ field }) => (
                                <Select
                                    menuPlacement='auto'
                                    options={LOCATION_OPTIONS}
                                    value={
                                        LOCATION_OPTIONS.find(
                                            (o) => o.value === field.value,
                                        ) ?? null
                                    }
                                    onChange={(opt) =>
                                        field.onChange(opt ? opt.value : '')
                                    }
                                    placeholder='Select a state or Treatment Abroad...'
                                    isClearable
                                />
                            )}
                        />
                        {errors.location && (
                            <p className='mt-1 text-xs text-red'>
                                {errors.location.message}
                            </p>
                        )}
                    </div>

                    <div>
                        <label className='mb-1 block text-sm font-medium'>
                            Zip
                        </label>
                        <input
                            {...register('zipcode', { required: true })}
                            type='text'
                            className='w-full rounded border border-gray-300 px-3 py-2'
                            placeholder='Zip'
                        />
                        {errors.zipcode && (
                            <p className='mt-1 text-xs text-red'>
                                {errors.zipcode.message}
                            </p>
                        )}
                    </div>
                    <div className='mb-4 mt-4'>
                        <label className='mb-1 block text-sm font-medium'>
                            URL
                        </label>
                        <input
                            {...register('url', { required: true })}
                            type='text'
                            className='w-full rounded border border-gray-300 px-3 py-2'
                            placeholder='https://example.com'
                        />
                        {errors.url && (
                            <p className='mt-1 text-xs text-red'>
                                {errors.url.message}
                            </p>
                        )}
                    </div>

                    <div className='mb-4 mt-4'>
                        <label className='mb-1 block text-sm font-medium'>
                            NPI Number
                        </label>
                        <input
                            {...register('npi_num')}
                            type='text'
                            className='w-full rounded border border-gray-300 px-3 py-2'
                            placeholder='NPI Number'
                        />
                        {errors.npi_num && (
                            <p className='mt-1 text-xs text-red'>
                                {errors.npi_num.message}
                            </p>
                        )}
                    </div>

                    <div className='flex justify-between'>
                        <button
                            type='button'
                            onClick={() => setIsOpen(false)}
                            className='m-auth mr-3 w-full rounded-full px-[24px] py-[10px] text-[14px]/[18px] font-bold uppercase outline transition-colors'
                        >
                            Cancel
                        </button>
                        <button
                            type='submit'
                            disabled={saving}
                            style={{
                                backgroundColor: 'rgb(234 179 8)',
                            }}
                            className='m-auto mr-3 w-full rounded-full bg-warning px-[24px] py-[10px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:bg-[#e6f7f8]'
                        >
                            {saving
                                ? 'Saving...'
                                : isCreateMode
                                ? 'Add Medical Center'
                                : 'Save'}
                        </button>
                    </div>
                </form>
            </div>
        </Drawer>
    );
};

export default EditMedicalCenterDrawer;
